import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import ScrollAnimation from '../../components/base/ScrollAnimation';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';
import { useSEO, generateServiceSchema, generateFAQSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function CommercialTintingPage() {
  const tintTypes = [
    {
      name: 'Storefront Window Tinting',
      image: 'https://public.readdy.ai/ai/img_res/bd5282bd2c6f4fa14a71acc9f631c585.jpg',
      features: [
        'Enhanced privacy for customers and employees',
        'Reduces glare on computer screens',
        'UV protection prevents merchandise fading',
        'Energy savings up to 30%',
        'Professional appearance',
        'Customizable tint levels',
      ],
      benefits: 'Perfect for retail stores, offices, and showrooms. Create a comfortable shopping environment while protecting your inventory.',
    },
    {
      name: 'Office Building Tinting',
      image: 'https://readdy.ai/api/search-image?query=modern%20office%20building%20exterior%20with%20tinted%20windows%2C%20corporate%20architecture%2C%20glass%20facade%2C%20professional%20business%20setting%2C%20clean%20simple%20background%2C%20architectural%20photography%20style&width=800&height=600&seq=commercial-office-002&orientation=landscape',
      features: [
        'Improved employee comfort and productivity',
        'Significant energy cost reduction',
        'Consistent exterior appearance',
        'Blocks 99% of harmful UV rays',
        'Reduces HVAC workload',
        'Professional installation with minimal disruption',
      ],
      benefits: 'Ideal for multi-story buildings and corporate offices. Enhance workplace comfort while reducing operational costs.',
    },
    {
      name: 'Restaurant & Hospitality',
      image: 'https://readdy.ai/api/search-image?query=upscale%20restaurant%20interior%20with%20large%20tinted%20windows%2C%20elegant%20dining%20space%2C%20natural%20light%20filtering%20through%20windows%2C%20professional%20hospitality%20setting%2C%20clean%20simple%20background&width=800&height=600&seq=commercial-restaurant-003&orientation=landscape',
      features: [
        'Creates comfortable dining atmosphere',
        'Protects furniture and decor from fading',
        'Reduces cooling costs significantly',
        'Maintains natural light while blocking heat',
        'Privacy without sacrificing views',
        'Enhances customer experience',
      ],
      benefits: 'Essential for restaurants, hotels, and cafes. Keep guests comfortable while protecting your interior investments.',
    },
    {
      name: 'Medical & Healthcare Facilities',
      image: 'https://readdy.ai/api/search-image?query=modern%20medical%20clinic%20waiting%20room%20with%20tinted%20windows%2C%20healthcare%20facility%20interior%2C%20clean%20professional%20environment%2C%20natural%20lighting%2C%20simple%20clean%20background%20highlighting%20patient%20comfort&width=800&height=600&seq=commercial-medical-004&orientation=landscape',
      features: [
        'HIPAA-compliant privacy solutions',
        'Reduces patient discomfort from glare',
        'Maintains sterile appearance',
        'Energy efficient climate control',
        'UV protection for sensitive equipment',
        'Creates calming environment',
      ],
      benefits: 'Specialized solutions for clinics, hospitals, and dental offices. Ensure patient privacy and comfort.',
    },
  ];

  const benefits = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Reduce Energy Costs',
      description: 'Save up to 30% on cooling costs by blocking solar heat. Window tinting pays for itself through energy savings.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'UV Protection',
      description: 'Block 99% of harmful UV rays that cause fading and damage to furniture, merchandise, and flooring.',
    },
    {
      icon: 'ri-eye-off-line',
      title: 'Enhanced Privacy',
      description: 'Maintain visibility from inside while preventing outsiders from seeing in. Perfect for ground-floor offices.',
    },
    {
      icon: 'ri-temp-cold-line',
      title: 'Climate Control',
      description: 'Reduce hot spots and maintain consistent temperatures throughout your space. Improve HVAC efficiency.',
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Safety & Security',
      description: 'Security film holds shattered glass together, protecting against break-ins and accidents.',
    },
    {
      icon: 'ri-building-line',
      title: 'Professional Appearance',
      description: 'Uniform exterior appearance enhances your building\'s curb appeal and professional image.',
    },
  ];

  const faqs = [
    {
      question: 'What types of buildings do you tint?',
      answer: 'We tint all types of commercial properties including office buildings, retail stores, restaurants, medical facilities, schools, and warehouses. We also offer residential window tinting for homes and apartments.',
    },
    {
      question: 'How long does commercial window tinting take?',
      answer: 'Installation time varies based on building size and number of windows. Small offices may take 1-2 days, while larger buildings can take several days. We work efficiently to minimize disruption to your business operations.',
    },
    {
      question: 'Will window tinting affect my HVAC costs?',
      answer: 'Yes! Commercial window tinting can reduce cooling costs by up to 30% by blocking heat and UV rays. Many businesses see a return on investment within 2-3 years through energy savings alone.',
    },
    {
      question: 'Do you offer mobile service for commercial properties?',
      answer: 'Yes! We bring our professional equipment to your location for convenient installation. Learn more about our mobile tinting services that come to you anywhere in Baltimore and surrounding areas.',
    },
    {
      question: 'Can window tinting improve security?',
      answer: 'Absolutely! Security film holds glass together if broken, deterring break-ins and protecting against accidents. It also provides privacy while maintaining natural light in your workspace.',
    },
    {
      question: 'What warranty do you offer on commercial tinting?',
      answer: 'We offer comprehensive warranties on all commercial installations, typically 10-15 years depending on the film type. Our premium films come with manufacturer warranties covering bubbling, peeling, and discoloration.',
    },
    {
      question: 'Do you work after business hours?',
      answer: 'Yes! We understand business operations can\'t stop. We offer flexible scheduling including evenings and weekends to minimize disruption to your business.',
    },
    {
      question: 'Can you tint vehicle fleets as well?',
      answer: 'Yes! We specialize in fleet services for businesses with multiple vehicles. We also offer vehicle decals and graphics for company branding. Contact us for volume pricing on fleet tinting.',
    },
  ];

  useSEO({
    title: 'Commercial Window Tinting Baltimore - Office & Building Tint | U.B.K',
    description: 'Professional commercial window tinting in Baltimore, MD. Reduce energy costs, increase privacy, and protect interiors. Office buildings, retail stores, and more. Free consultation. Call (443) 800-0348.',
    keywords: 'commercial window tinting Baltimore, office tinting Maryland, building window film, energy efficient tinting, UV protection',
    canonical: '/commercial-tinting',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateServiceSchema(
          'Commercial Window Tinting',
          'Professional window tinting for commercial buildings. Reduce energy costs, increase privacy, and protect interiors with premium window film.',
          '/commercial-tinting'
        ),
        generateFAQSchema(faqs),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Commercial Tinting', url: '/commercial-tinting' },
        ]),
      ],
    },
  });

  return (
    <div className="min-h-screen">
      <Navigation />
      <main className="pt-24 max-md:pt-20">
        {/* Hero Section */}
        <section className="relative pt-12 md:pt-16 pb-8 md:pb-12 bg-gradient-to-br from-secondary via-gray-800 to-secondary overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'linear-gradient(45deg, transparent 48%, white 49%, white 51%, transparent 52%)',
              backgroundSize: '20px 20px',
            }}></div>
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12 items-center">
              <div className="text-white">
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-3 md:mb-6 leading-tight">
                  Commercial Window Tinting Solutions
                </h1>
                <p className="text-sm md:text-xl text-gray-300 mb-4 md:mb-8 leading-relaxed">
                  Professional window tinting for businesses, offices, and commercial properties. Reduce energy costs, enhance privacy, and protect your investment.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 md:gap-4">
                  <Link to="/contact">
                    <Button size="lg" className="text-xs md:text-base py-2 md:py-3 px-4 md:px-6 whitespace-nowrap">
                      <i className="ri-calendar-line mr-1 md:mr-2"></i>
                      Schedule Consultation
                    </Button>
                  </Link>
                  <a href="tel:4438000348">
                    <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-secondary text-xs md:text-base py-2 md:py-3 px-4 md:px-6 whitespace-nowrap">
                      <i className="ri-phone-line mr-1 md:mr-2"></i>
                      Call for Quote
                    </Button>
                  </a>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20commercial%20building%20with%20modern%20tinted%20windows%2C%20corporate%20office%20exterior%2C%20glass%20facade%20architecture%2C%20business%20district%20setting%2C%20professional%20photography%2C%20clean%20simple%20background%20highlighting%20the%20building&width=800&height=600&seq=commercial-hero-main&orientation=landscape"
                  alt="Commercial Window Tinting"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Commercial Solutions Section */}
        <section className="py-8 md:py-24 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollAnimation animation="fade-in">
              <div className="text-center mb-8 md:mb-16">
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-secondary mb-2 md:mb-4">
                  Commercial Tinting Solutions
                </h2>
                <p className="text-sm md:text-xl text-gray-600 max-w-3xl mx-auto">
                  Customized window tinting solutions for every type of commercial property. Call us for a free quote tailored to your needs.
                </p>
              </div>
            </ScrollAnimation>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8">
              {tintTypes.map((tint, index) => (
                <ScrollAnimation key={tint.name} animation="slide-up" delay={index * 100}>
                  <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 h-full flex flex-col">
                    <div className="relative h-40 md:h-64">
                      <img
                        src={tint.image}
                        alt={tint.name}
                        className="w-full h-full object-cover object-top"
                      />
                      <div className="absolute top-2 md:top-4 right-2 md:right-4 bg-primary text-white px-2 md:px-4 py-1 md:py-2 rounded-full font-bold text-xs md:text-base whitespace-nowrap">
                        Call for Quote
                      </div>
                    </div>
                    
                    <div className="p-4 md:p-6 flex-1 flex flex-col">
                      <h3 className="text-lg md:text-2xl font-bold text-secondary mb-2 md:mb-4">{tint.name}</h3>
                      <p className="text-xs md:text-base text-gray-600 mb-3 md:mb-6 leading-relaxed">{tint.benefits}</p>
                      
                      <div className="space-y-1.5 md:space-y-3 mb-4 md:mb-6 flex-1">
                        {tint.features.map((feature) => (
                          <div key={feature} className="flex items-start space-x-2 md:space-x-3">
                            <i className="ri-check-line text-primary text-base md:text-xl mt-0.5 w-4 h-4 md:w-5 md:h-5 flex items-center justify-center flex-shrink-0"></i>
                            <span className="text-xs md:text-base text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                      
                      <a href="tel:4438000348">
                        <Button fullWidth className="text-xs md:text-base py-2 md:py-3 whitespace-nowrap">
                          <i className="ri-phone-line mr-1 md:mr-2"></i>
                          Call for Quote
                        </Button>
                      </a>
                    </div>
                  </div>
                </ScrollAnimation>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-8 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollAnimation animation="fade-in">
              <div className="text-center mb-8 md:mb-16">
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-secondary mb-2 md:mb-4">
                  Why Choose Commercial Window Tinting?
                </h2>
                <p className="text-sm md:text-xl text-gray-600 max-w-3xl mx-auto">
                  Protect your business investment while improving comfort and reducing costs.
                </p>
              </div>
            </ScrollAnimation>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
              {benefits.map((benefit, index) => (
                <ScrollAnimation key={benefit.title} animation="slide-up" delay={index * 100}>
                  <div className="bg-gradient-to-br from-gray-50 to-white p-4 md:p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                    <div className="w-10 h-10 md:w-16 md:h-16 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mb-2 md:mb-4">
                      <i className={`${benefit.icon} text-white text-lg md:text-3xl`}></i>
                    </div>
                    <h3 className="text-base md:text-xl font-bold text-secondary mb-1.5 md:mb-3">{benefit.title}</h3>
                    <p className="text-xs md:text-base text-gray-600 leading-relaxed">{benefit.description}</p>
                  </div>
                </ScrollAnimation>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-8 md:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollAnimation animation="fade-in">
              <div className="text-center mb-8 md:mb-16">
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-secondary mb-2 md:mb-4">
                  Our Commercial Installation Process
                </h2>
                <p className="text-sm md:text-xl text-gray-600 max-w-3xl mx-auto">
                  Professional service from consultation to completion with minimal disruption to your business.
                </p>
              </div>
            </ScrollAnimation>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
              {[
                {
                  step: '01',
                  title: 'Free Consultation',
                  description: 'We assess your property, discuss your needs, and provide a detailed quote. No obligation.',
                  icon: 'ri-chat-check-line',
                },
                {
                  step: '02',
                  title: 'Custom Solution',
                  description: 'We recommend the best film type and shade for your specific requirements and budget.',
                  icon: 'ri-lightbulb-line',
                },
                {
                  step: '03',
                  title: 'Professional Install',
                  description: 'Our certified technicians complete installation with minimal disruption to your operations.',
                  icon: 'ri-tools-line',
                },
                {
                  step: '04',
                  title: 'Quality Guarantee',
                  description: 'Final inspection and warranty documentation. We ensure your complete satisfaction.',
                  icon: 'ri-shield-check-line',
                },
              ].map((process, index) => (
                <ScrollAnimation key={process.step} animation="slide-up" delay={index * 100}>
                  <div className="text-center">
                    <div className="w-12 h-12 md:w-20 md:h-20 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-2 md:mb-4">
                      <i className={`${process.icon} text-white text-xl md:text-3xl`}></i>
                    </div>
                    <div className="text-2xl md:text-4xl font-bold text-gray-200 mb-1 md:mb-2">{process.step}</div>
                    <h3 className="text-base md:text-xl font-bold text-secondary mb-1.5 md:mb-3">{process.title}</h3>
                    <p className="text-xs md:text-base text-gray-600 leading-relaxed">{process.description}</p>
                  </div>
                </ScrollAnimation>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-8 md:py-24 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollAnimation animation="fade-in">
              <div className="text-center mb-8 md:mb-16">
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-secondary mb-2 md:mb-4">
                  Frequently Asked Questions
                </h2>
                <p className="text-sm md:text-xl text-gray-600">
                  Everything you need to know about commercial tinting
                </p>
              </div>
            </ScrollAnimation>

            <div className="space-y-2 md:space-y-4">
              {faqs.map((faq, index) => (
                <ScrollAnimation key={index} animation="slide-up" delay={index * 50}>
                  <details className="bg-white rounded-xl shadow-md overflow-hidden group">
                    <summary className="px-4 md:px-6 py-3 md:py-5 cursor-pointer list-none flex items-center justify-between hover:bg-gray-50 transition-colors">
                      <h3 className="text-sm md:text-lg font-semibold text-secondary pr-4">{faq.question}</h3>
                      <i className="ri-arrow-down-s-line text-xl md:text-2xl text-primary group-open:rotate-180 transition-transform w-5 h-5 md:w-6 md:h-6 flex items-center justify-center flex-shrink-0"></i>
                    </summary>
                    <div className="px-4 md:px-6 pb-3 md:pb-5 text-xs md:text-base text-gray-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                </ScrollAnimation>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-8 md:py-24 bg-gradient-to-br from-primary to-accent text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <ScrollAnimation animation="fade-in">
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-6">
                Ready to Upgrade Your Commercial Property?
              </h2>
              <p className="text-sm md:text-xl mb-4 md:mb-8 opacity-90">
                Get a free consultation and custom quote for your business. Professional installation with flexible scheduling.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 md:gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" variant="secondary" className="min-w-[160px] md:min-w-[200px] text-xs md:text-base py-2 md:py-3 whitespace-nowrap">
                    <i className="ri-calendar-line mr-1 md:mr-2"></i>
                    Schedule Consultation
                  </Button>
                </Link>
                <a href="tel:4438000348">
                  <Button size="lg" variant="outline" className="min-w-[160px] md:min-w-[200px] border-white text-white hover:bg-white hover:text-primary text-xs md:text-base py-2 md:py-3 whitespace-nowrap">
                    <i className="ri-phone-line mr-1 md:mr-2"></i>
                    (443) 800-0348
                  </Button>
                </a>
              </div>
            </ScrollAnimation>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
